import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, Copy, FileText, Loader2, Play, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useAssessmentFixture,
  useCloneAssessmentFixture,
  useCreateAssessmentFixture,
  useDeleteAssessmentFixture,
  useEvalSweepAssessmentFixture,
  useReplaceAssessmentFixtureArticle,
  useRunAssessmentFixture,
  useUpdateAssessmentFixture,
} from "@/hooks/admin/assessmentFixtures";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { AssessmentFixtureRunSummary, AssessmentRunStatus } from "@/types";

const NEW_FIXTURE_ID = "new";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusTone(status: AssessmentRunStatus): BadgeTone {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "danger";
    case "pending":
      return "warning";
  }
}

const fixtureFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  topic_title: z.string().trim().min(1, "Topic title is required"),
  topic_description: z.string().trim().min(1, "Topic description is required"),
  notes: z.string(),
});
type FixtureFormValues = z.infer<typeof fixtureFormSchema>;

const EMPTY_FORM: FixtureFormValues = {
  name: "",
  topic_title: "",
  topic_description: "",
  notes: "",
};

export default function AdminAssessmentFixtureDetailPage() {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const navigate = useNavigate();
  const isNew = fixtureId === NEW_FIXTURE_ID;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FixtureFormValues>({
    resolver: zodResolver(fixtureFormSchema),
    defaultValues: EMPTY_FORM,
  });
  const { control } = form;

  const [articleFile, setArticleFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fixtureQuery = useAssessmentFixture(isNew ? undefined : fixtureId);
  const fixture = fixtureQuery.data;
  const loading = !isNew && fixtureQuery.isPending;
  const title = fixture?.name ?? "";
  const articleFilename = fixture?.article_filename ?? null;
  const hasArticle = fixture?.has_article ?? false;
  const runs: AssessmentFixtureRunSummary[] = fixture?.runs ?? [];
  const runCount = fixture?.run_count ?? 0;

  const createFixture = useCreateAssessmentFixture();
  const updateFixture = useUpdateAssessmentFixture();
  const replaceArticle = useReplaceAssessmentFixtureArticle();
  const runFixture = useRunAssessmentFixture();
  const evalSweep = useEvalSweepAssessmentFixture();
  const cloneFixture = useCloneAssessmentFixture();
  const deleteFixture = useDeleteAssessmentFixture();

  const saving =
    createFixture.isPending ||
    updateFixture.isPending ||
    replaceArticle.isPending ||
    runFixture.isPending ||
    evalSweep.isPending ||
    cloneFixture.isPending ||
    deleteFixture.isPending;

  // Surface a load failure inline (the page renders without the table on error).
  const loadError =
    !isNew && fixtureQuery.isError
      ? fixtureQuery.error instanceof Error
        ? fixtureQuery.error.message
        : "Failed to load fixture"
      : null;

  // Reset the form whenever the loaded fixture changes.
  useEffect(() => {
    if (fixture) {
      form.reset({
        name: fixture.name,
        topic_title: fixture.topic_title,
        topic_description: fixture.topic_description,
        notes: fixture.notes ?? "",
      });
    }
  }, [fixture, form]);

  const doSave = async (values: FixtureFormValues, alsoRun: boolean) => {
    if (isNew && !articleFile) {
      setError("Article PDF is required");
      return;
    }
    setError(null);
    try {
      if (isNew && articleFile) {
        const { fixture: created, run_id } = await createFixture.mutateAsync({
          payload: {
            name: values.name,
            topic_title: values.topic_title,
            topic_description: values.topic_description,
            notes: values.notes || null,
            article_pdf: articleFile,
          },
          runImmediately: alsoRun,
        });
        if (alsoRun && run_id) {
          navigate(`/admin/assessment-fixtures/runs/${run_id}`);
        } else {
          navigate(`/admin/assessment-fixtures/${created.id}`);
        }
        return;
      }

      if (!fixtureId) return;
      await updateFixture.mutateAsync({
        id: fixtureId,
        patch: {
          name: values.name,
          topic_title: values.topic_title,
          topic_description: values.topic_description,
          notes: values.notes || null,
        },
      });
      if (alsoRun) {
        const { run_id } = await runFixture.mutateAsync({ id: fixtureId });
        navigate(`/admin/assessment-fixtures/runs/${run_id}`);
        return;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const handleSave = (alsoRun: boolean) => form.handleSubmit((values) => doSave(values, alsoRun))();

  const handleReplaceArticle = async (file: File) => {
    if (!fixtureId || isNew) return;
    setError(null);
    try {
      await replaceArticle.mutateAsync({ id: fixtureId, file });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to replace article");
    }
  };

  const handleClone = async () => {
    if (!fixtureId || isNew) return;
    try {
      const cloned = await cloneFixture.mutateAsync(fixtureId);
      navigate(`/admin/assessment-fixtures/${cloned.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clone");
    }
  };

  const handleDelete = async () => {
    if (!fixtureId || isNew) return;
    try {
      await deleteFixture.mutateAsync(fixtureId);
      navigate("/admin/assessment-fixtures");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    }
  };

  const runColumns: ColumnDef<AssessmentFixtureRunSummary>[] = [
    {
      accessorKey: "ran_at",
      header: "Ran at",
      cell: ({ row }) => <span className="text-gray-600">{formatDate(row.original.ran_at)}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge tone={statusTone(row.original.status)}>{row.original.status}</StatusBadge>
      ),
    },
    {
      accessorKey: "final_score",
      header: "Score",
      cell: ({ row }) => <span className="text-gray-600">{row.original.final_score ?? "—"}</span>,
    },
    {
      accessorKey: "criterion_key",
      header: "Scope",
      cell: ({ row }) => (
        <span className="text-gray-600">{row.original.criterion_key ?? "all"}</span>
      ),
    },
    {
      accessorKey: "prompt_version",
      header: "Prompt version",
      cell: ({ row }) => (
        <span className="text-gray-600">{row.original.prompt_version ?? "—"}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => navigate(`/admin/assessment-fixtures/runs/${row.original.id}`)}
          className="text-xs font-medium text-green-600 hover:underline"
        >
          View
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl">
        <button
          type="button"
          onClick={() => navigate("/admin/assessment-fixtures")}
          className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to fixtures
        </button>

        <h1 className="text-2xl font-semibold mb-6 text-white">
          {isNew ? "New Assessment Fixture" : title}
        </h1>

        {(error ?? loadError) && (
          <div className="mb-4 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
            {error ?? loadError}
          </div>
        )}

        <Form {...form}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="bg-white rounded-lg border p-6 space-y-4"
          >
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Climate — strong A1 example" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="topic_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Topic title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="topic_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Topic description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <FormLabel className="mb-1 block">Article (PDF)</FormLabel>
              {isNew ? (
                <div>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => setArticleFile(e.target.files?.[0] ?? null)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 hover:bg-gray-50 text-sm text-gray-700"
                  >
                    <Upload className="w-4 h-4" />
                    {articleFile ? articleFile.name : "Choose PDF"}
                  </button>
                  {articleFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      {(articleFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-sm text-gray-700 flex-1">
                    <FileText className="w-4 h-4 text-gray-500" />
                    {hasArticle ? (articleFilename ?? "article.pdf") : "No article uploaded"}
                  </div>
                  <Input
                    ref={replaceInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleReplaceArticle(f);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => replaceInputRef.current?.click()}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-medium disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" /> Replace
                  </button>
                </div>
              )}
            </div>

            <FormField
              control={control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleSave(false)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-medium disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleSave(true)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium disabled:opacity-50"
              >
                <Play className="w-4 h-4" /> Save & Run
              </button>
              {!isNew && (
                <>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleClone}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-medium disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4" /> Clone
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setConfirmDelete(true)}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium disabled:opacity-50 ml-auto"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </>
              )}
            </div>
          </form>
        </Form>

        {!isNew && fixtureId && (
          <div className="mt-8 mb-6 bg-white rounded-lg border p-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 mr-2">Run single criterion:</span>
            {[
              { key: "analytical_reasoning", label: "Analytical Reasoning" },
              { key: "research_integrity", label: "Research Integrity" },
              { key: "relevance_and_investment_impact", label: "Relevance & Impact" },
              { key: "clarity_and_communication", label: "Clarity & Comm." },
              { key: "professional_and_ethical_standards", label: "Professional & Ethical" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                disabled={saving}
                onClick={async () => {
                  setError(null);
                  try {
                    const { run_id } = await runFixture.mutateAsync({
                      id: fixtureId,
                      criterionKey: c.key,
                    });
                    navigate(`/admin/assessment-fixtures/runs/${run_id}`);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Failed to run");
                  }
                }}
                className="px-3 py-1.5 rounded text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:opacity-50"
              >
                {c.label}
              </button>
            ))}
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                setError(null);
                try {
                  await evalSweep.mutateAsync({ id: fixtureId, n: 5 });
                  navigate(`/admin/assessment-fixtures/${fixtureId}/eval`);
                } catch (e) {
                  setError(e instanceof Error ? e.message : "Failed to start eval sweep");
                }
              }}
              className="ml-auto px-3 py-1.5 rounded text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            >
              Eval sweep (N=5)
            </button>
            <button
              type="button"
              onClick={() => navigate(`/admin/assessment-fixtures/${fixtureId}/eval`)}
              className="px-3 py-1.5 rounded text-xs font-medium bg-white border text-gray-700 hover:bg-gray-50"
            >
              View eval summary
            </button>
          </div>
        )}

        {!isNew && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-3 text-white">Run history ({runCount})</h2>
            <DataTable
              columns={runColumns}
              data={runs}
              emptyMessage="No runs yet."
              getRowId={(r) => r.id}
            />
          </div>
        )}

        {confirmDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Delete fixture?</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will also delete <strong>{runCount}</strong> run
                {runCount === 1 ? "" : "s"} associated with this fixture. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
